import { ErrorAlert } from 'components/ErrorAlert';
import { JAlertsContextProvider } from 'components/JAlerts/JAlertsContext';
import { LoadingOverlay } from 'components/LoadingOverlay';
import {
  trafficCampaignsRootPath,
  trafficProjectAssetsPath,
  trafficProjectEventsPath,
  trafficProjectJASAssetsPath,
} from 'components/Paths';
import { Feature } from 'features/flags';
import { createJasComponent } from 'features/mfe/components/JasComponent';
import { useTrafficProjectQuery } from 'generated/graphql';
import type { FC } from 'react';
import { Container } from 'react-bootstrap';
import { generatePath, Redirect, Route, Switch, useParams } from 'react-router-dom';
import type { AssetBrowser } from 'types/JourneysAssetSystemComponents';
import { stylesheet } from 'typestyle';
import { CampaignConfigProvider } from '../campaigns/config/CampaignConfigProvider';
import { CampaignsListPageContextProvider } from '../campaigns/TPCampaignsTab/CampaignsListPageContext';
import { CampaignsTable } from '../campaigns/TPCampaignsTab/CampaignsTable';
import { TPAssetsTab } from './TPAssetsTab';
import { TPEventsTab } from './TPEventsTab';
import { JagList } from './TPJASAssetsTab/JagList';
import { TPPageTopBar } from './TPPageTopBar';

const css = stylesheet({
  errorContainer: {
    paddingTop: '20px',
  },
});

const AssetBrowserComponent = createJasComponent<typeof AssetBrowser>('AssetBrowser');

export const TPPage: FC = () => {
  const { trafficProjectId } = useParams<{
    trafficProjectId: string;
  }>();
  const { data, loading, error } = useTrafficProjectQuery({
    variables: {
      id: trafficProjectId,
    },
  });
  return (
    <CampaignConfigProvider>
      <Container>
        {error && (
          <div className={css.errorContainer}>
            <ErrorAlert contextMessage="Unable to load traffic project" error={error} />
          </div>
        )}
        {loading && <LoadingOverlay dataCy="loading spinner" visible />}
        {!error && !loading && (
          <CampaignsListPageContextProvider hasEvent={!!(data?.trafficProject?.events && data?.trafficProject?.events.length > 0)}>
            <JAlertsContextProvider>
              <TPPageTopBar projectId={trafficProjectId} />
            </JAlertsContextProvider>
            <Switch>
              {/* Campaigns List Tab/Page */}
              <Route exact path={trafficCampaignsRootPath}>
                <JAlertsContextProvider>
                  <CampaignsTable />
                </JAlertsContextProvider>
              </Route>
              {/* Campaigns Assets Tab/Page */}
              <Route exact path={trafficProjectAssetsPath}>
                <Feature fallback={<TPAssetsTab />} feature="JOURNEYS_ASSET_BROWSER">
                  <AssetBrowserComponent />
                </Feature>
              </Route>
              {/* Campaigns Events Tab/Page */}
              <Route exact path={trafficProjectEventsPath}>
                <TPEventsTab />
              </Route>
              {/* Campaigns JAS Assets Tab/Page */}
              <Route exact path={trafficProjectJASAssetsPath}>
                <JagList />
              </Route>

              <Route>
                <Redirect
                  to={generatePath(trafficCampaignsRootPath, {
                    trafficProjectId,
                  })}
                />
              </Route>
            </Switch>
          </CampaignsListPageContextProvider>
        )}
      </Container>
    </CampaignConfigProvider>
  );
};
